import { useMemo, useState } from 'react';

import type {
  ArInvoiceWithLines,
  ChartOfAccount,
  CreateArCreditNote,
  CreateArCreditNoteLine,
} from '@dcc-erp/shared';
import { AR_CREDIT_NOTE_REASONS, dateToInputValue, fmt, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { modalBody, modalError, modalFooter, modalLabel } from './modal-style';
import {
  useActiveCompanyId,
  useCoa,
  useCreateArCreditNote,
  useSubmitArCreditNote,
} from '../../lib/queries';
import { AppModal } from '../AppModal';

// =============================================================================
// Create AR Credit Note (ใบลดหนี้) from an invoice.
//
// CreateCreditNoteModal (container) owns the COA fetch + the two-step
// create-then-submit engine flow; the inner CreateCreditNoteForm
// (presentational, router/data-free) owns the reason select + line grid +
// totals bar so the submit payload is unit-testable without a query client.
// Mirrors the CreateInvoiceModal line-grid markup + RecordReceiptForm split.
// Plain prototype.css classes / inline modal-style — no tailwind.
// =============================================================================

// Reason options sourced from the shared preset table so the codes stay in one
// place (reasonCode drives RD typing/reporting; label is display-only).
const reasonOptions = Object.entries(AR_CREDIT_NOTE_REASONS).map(([value, label]) => ({
  value,
  label,
}));

// The form only reads the invoice's id + vatRate + revenue codes; accept the
// full detail shape but depend on the lean subset so tests pass lean fixtures.
type CreditNoteInvoice = Pick<ArInvoiceWithLines, 'id'> & {
  vatRate: string | number;
  lines?: { revenueCode: string }[];
};
type RevenueAccount = Pick<ChartOfAccount, 'code' | 'nameTh'>;

// Wire line + a client-only stable key (`_uid`). Stripped before submit.
type LineRow = CreateArCreditNoteLine & { _uid: string };

// The form emits this wire shape; the container parses it into CreateArCreditNote.
export interface CreateCreditNotePayload {
  creditNoteDate: string;
  invoiceId: string;
  reasonCode: string;
  reasonNote?: string;
  vatRate: number;
  lines: CreateArCreditNoteLine[];
}

export interface CreateCreditNoteFormProps {
  invoice: CreditNoteInvoice;
  revenueAccounts: RevenueAccount[];
  onSubmit: (payload: CreateCreditNotePayload) => void;
  submitting: boolean;
}

export function CreateCreditNoteForm({
  invoice,
  revenueAccounts,
  onSubmit,
  submitting,
}: CreateCreditNoteFormProps) {
  // Seed a starting line from the invoice's first revenue code so the credit
  // posts back to the same revenue account by default.
  const defaultRevenueCode = invoice.lines?.[0]?.revenueCode ?? '4110';

  // Each line carries a stable synthetic `_uid` used only as the React list key,
  // so removing a middle row doesn't reassign a row's identity to its neighbour
  // (index keys would). `_uid` is never part of the wire payload — cleanLines
  // strips it back to the CreateArCreditNoteLine shape before submit.
  const [creditNoteDate, setCreditNoteDate] = useState(dateToInputValue(new Date()));
  const [reasonCode, setReasonCode] = useState<string>(reasonOptions[0]?.value ?? '');
  const [reasonNote, setReasonNote] = useState('');
  // Default the credit note's VAT to the invoice's own rate.
  const [vatRate, setVatRate] = useState<number>(toNumber(invoice.vatRate));
  const [lines, setLines] = useState<LineRow[]>(() => [
    {
      _uid: crypto.randomUUID(),
      description: '',
      qty: 1,
      unitPrice: 0,
      revenueCode: defaultRevenueCode,
    },
  ]);

  const subtotal = useMemo(
    () => lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unitPrice || 0), 0),
    [lines],
  );
  const vatAmount = useMemo(() => (subtotal * vatRate) / 100, [subtotal, vatRate]);
  const totalAmount = subtotal + vatAmount;

  function updateLine(idx: number, patch: Partial<CreateArCreditNoteLine>) {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }

  function addLine() {
    setLines((prev) => [
      ...prev,
      {
        _uid: crypto.randomUUID(),
        description: '',
        qty: 1,
        unitPrice: 0,
        revenueCode: defaultRevenueCode,
      },
    ]);
  }

  function removeLine(idx: number) {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  }

  // Only well-formed lines reach the payload (desc + positive qty + non-neg
  // price) so a half-typed row never POSTs. Also strips the client-only `_uid`
  // back to the wire shape.
  const cleanLines = useMemo<CreateArCreditNoteLine[]>(
    () =>
      lines
        .filter((l) => l.description && Number(l.qty) > 0 && Number(l.unitPrice) >= 0)
        .map((l) => ({
          description: l.description,
          qty: l.qty,
          unitPrice: l.unitPrice,
          revenueCode: l.revenueCode,
        })),
    [lines],
  );

  const disabled =
    !reasonCode || !creditNoteDate || cleanLines.length === 0 || subtotal <= 0 || submitting;

  function handleSubmit() {
    onSubmit({
      creditNoteDate,
      invoiceId: invoice.id,
      reasonCode,
      ...(reasonNote ? { reasonNote } : {}),
      vatRate,
      lines: cleanLines,
    });
  }

  return (
    <div style={modalBody}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px', gap: 12 }}>
        <div>
          <label style={modalLabel}>วันที่ออกใบลดหนี้</label>
          <input
            type="date"
            className="input"
            aria-label="cn-date"
            value={creditNoteDate}
            onChange={(e) => setCreditNoteDate(e.target.value)}
            style={{ width: '100%', height: 38, fontSize: 14 }}
          />
        </div>
        <div>
          <label style={modalLabel}>เหตุผล</label>
          <select
            className="select"
            aria-label="cn-reason"
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}
            style={{ width: '100%', height: 38, fontSize: 14 }}
          >
            {reasonOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={modalLabel}>VAT (%)</label>
          <input
            type="number"
            className="input tnum"
            aria-label="cn-vat-rate"
            value={vatRate}
            onChange={(e) => setVatRate(Number(e.target.value))}
            min={0}
            max={100}
            step={0.01}
            style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
          />
        </div>
      </div>

      <div>
        <label style={modalLabel}>หมายเหตุ (optional)</label>
        <input
          className="input"
          aria-label="cn-reason-note"
          value={reasonNote}
          onChange={(e) => setReasonNote(e.target.value)}
          placeholder="รายละเอียดเพิ่มเติม"
          style={{ width: '100%', height: 38, fontSize: 14 }}
        />
      </div>

      <div>
        <label style={modalLabel}>รายการ</label>
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>คำอธิบาย</th>
                <th className="num" style={{ width: 80 }}>
                  จำนวน
                </th>
                <th className="num" style={{ width: 110 }}>
                  ราคา/หน่วย
                </th>
                <th style={{ width: 160 }}>บัญชีรายได้</th>
                <th className="num" style={{ width: 110 }}>
                  ยอดเงิน
                </th>
                <th style={{ width: 30 }} />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => {
                const amount = Number(l.qty || 0) * Number(l.unitPrice || 0);
                return (
                  <tr key={l._uid}>
                    <td>
                      <input
                        className="input"
                        aria-label={`cn-line-desc-${idx}`}
                        value={l.description}
                        onChange={(e) => updateLine(idx, { description: e.target.value })}
                        style={{ width: '100%', height: 32, fontSize: 13 }}
                      />
                    </td>
                    <td className="num">
                      <input
                        type="number"
                        className="input tnum"
                        aria-label={`cn-line-qty-${idx}`}
                        value={l.qty}
                        min={0}
                        step={0.0001}
                        onChange={(e) => updateLine(idx, { qty: Number(e.target.value) })}
                        style={{ width: '100%', height: 32, fontSize: 13, textAlign: 'right' }}
                      />
                    </td>
                    <td className="num">
                      <input
                        type="number"
                        className="input tnum"
                        aria-label={`cn-line-price-${idx}`}
                        value={l.unitPrice}
                        min={0}
                        step={0.01}
                        onChange={(e) => updateLine(idx, { unitPrice: Number(e.target.value) })}
                        style={{ width: '100%', height: 32, fontSize: 13, textAlign: 'right' }}
                      />
                    </td>
                    <td>
                      <select
                        className="select"
                        aria-label={`cn-line-revenue-${idx}`}
                        value={l.revenueCode}
                        onChange={(e) => updateLine(idx, { revenueCode: e.target.value })}
                        style={{ width: '100%', height: 32, fontSize: 13 }}
                      >
                        {revenueAccounts.length === 0 && (
                          <option value={l.revenueCode}>{l.revenueCode}</option>
                        )}
                        {revenueAccounts.map((a) => (
                          <option key={a.code} value={a.code}>
                            {a.code} · {a.nameTh}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="num tnum">{fmt(amount, 2)}</td>
                    <td>
                      {lines.length > 1 && (
                        <button
                          className="btn ghost"
                          onClick={() => removeLine(idx)}
                          style={{ padding: '0 6px' }}
                        >
                          <Icon name="x" size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <button className="btn ghost" onClick={addLine} style={{ marginTop: 8, fontSize: 13 }}>
          <Icon name="plus" size={12} /> เพิ่มรายการ
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          rowGap: 4,
          fontSize: 14,
          marginTop: 8,
        }}
      >
        <div className="muted">ยอดก่อน VAT</div>
        <div className="tnum" style={{ textAlign: 'right' }}>
          {fmt(subtotal, 2)}
        </div>
        <div className="muted">VAT ({vatRate}%)</div>
        <div className="tnum" style={{ textAlign: 'right' }}>
          {fmt(vatAmount, 2)}
        </div>
        <div style={{ fontWeight: 600 }}>รวมทั้งสิ้น</div>
        <div className="tnum" style={{ textAlign: 'right', fontWeight: 600 }}>
          {fmt(totalAmount, 2)}
        </div>
      </div>

      <div style={modalFooter}>
        <button type="button" className="btn primary" disabled={disabled} onClick={handleSubmit}>
          <Icon name="check" size={14} /> {submitting ? 'กำลังบันทึก…' : 'ออกใบลดหนี้'}
        </button>
      </div>
    </div>
  );
}

export interface CreateCreditNoteModalProps {
  invoice: ArInvoiceWithLines;
  onClose: () => void;
}

export function CreateCreditNoteModal({ invoice, onClose }: CreateCreditNoteModalProps) {
  const companyId = useActiveCompanyId();
  const create = useCreateArCreditNote(companyId);
  const submit = useSubmitArCreditNote(companyId);

  // REVENUE leaf accounts only — control/header accounts can't be posted to.
  const { data: coa = [] } = useCoa(companyId, true);
  const revenueAccounts = useMemo(
    () => coa.filter((c) => c.type === 'REVENUE' && c.isActive && !c.isControl),
    [coa],
  );

  const error = create.error ?? submit.error;
  const pending = create.isPending || submit.isPending;

  async function handleSubmit(payload: CreateCreditNotePayload) {
    // Two-step engine flow: create the DRAFT, then submit it to the approval
    // engine (the reversing JV is booked on final approval, not here).
    const data: CreateArCreditNote = {
      creditNoteDate: payload.creditNoteDate,
      invoiceId: payload.invoiceId,
      reasonCode: payload.reasonCode,
      ...(payload.reasonNote ? { reasonNote: payload.reasonNote } : {}),
      vatRate: payload.vatRate,
      lines: payload.lines,
    };
    const created = await create.mutateAsync({ data });
    await submit.mutateAsync(created.id);
    onClose();
  }

  // WS8 R2 pilot — migrated off the bespoke 760px ar/modal-style chrome to the
  // system-standard AppModal (1200px). Container-only: the presentational
  // CreateCreditNoteForm still owns the body + its in-body submit button (the
  // unit test renders the form directly and clicks that button), so the form is
  // unchanged and AppModal supplies the overlay/header/Escape behaviour.
  return (
    <AppModal title="ออกใบลดหนี้" onClose={onClose}>
      <CreateCreditNoteForm
        invoice={invoice}
        revenueAccounts={revenueAccounts}
        onSubmit={(payload) => void handleSubmit(payload)}
        submitting={pending}
      />
      {error && <div style={modalError}>บันทึกไม่สำเร็จ: {error.message}</div>}
    </AppModal>
  );
}
